import { useEffect, useState } from "react";
import { ExternalLink, Globe, Linkedin, Loader2, Newspaper, RefreshCw, Search, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/time";
import { Btn, WarmthBadge } from "@/components/sales/ui";

const List = ({ title, items, testid }) => (items?.length ? (
  <div data-testid={testid}><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
    <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm">{items.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
) : null);

const KIND = { website: [Globe, "Website"], linkedin: [Linkedin, "LinkedIn"], news: [Newspaper, "News"], web: [Search, "Web"] };

const Sources = ({ items, at }) => (
  <div data-testid="recent-activity">
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent activity online{at ? ` · checked ${fmtDate(at)}` : ""}</p>
    {!items?.length ? <p className="mt-1.5 text-sm text-muted-foreground" data-testid="recent-activity-empty">Nothing found online for this company.</p> : (
      <ul className="mt-1.5 space-y-1.5">{items.map((s, i) => {
        const [Icon, label] = KIND[s.kind] || KIND.web;
        return (
          <li key={s.url}>
            <a href={s.url} target="_blank" rel="noopener noreferrer" className="group flex gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-white/5" data-testid={`research-source-${i}`}>
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
              <span className="min-w-0 flex-1"><span className="flex items-center gap-1 font-medium"><span className="truncate">{s.title || s.url}</span><ExternalLink className="h-3 w-3 shrink-0 opacity-50 group-hover:opacity-100" /></span>
                <span className="block text-xs text-muted-foreground">{label}{s.date ? ` · ${s.date}` : ""}{s.snippet ? ` · ${s.snippet.slice(0, 140)}` : ""}</span></span>
            </a>
          </li>
        );
      })}</ul>
    )}
  </div>
);

const useAi = (path) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async (force = false) => {
    setBusy(true);
    setErr("");
    try { setData((await api.post(path, { force })).data); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  return { data, busy, err, go };
};

const Footer = ({ d, onRegen, busy }) => (
  <div className="flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
    <span>{d.cached ? "Saved briefing" : "Freshly generated"} · {fmtDate(d.generated_at)}</span>
    <button onClick={() => onRegen(true)} disabled={busy} className="flex items-center gap-1 hover:text-foreground" data-testid="ai-regenerate"><RefreshCw className="h-3 w-3" /> Regenerate</button>
  </div>
);

export const PrepareDialog = ({ cid, open, onClose }) => {
  const ai = useAi(`/sales/companies/${cid}/prepare`);
  const onOpen = (o) => { if (!o) onClose(); };
  useEffect(() => {
    if (open && !ai.data && !ai.busy && !ai.err) ai.go();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const d = ai.data;
  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" aria-describedby={undefined} data-testid="prepare-call-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-2xl"><Sparkles className="h-5 w-5 text-cyan-400" /> Prepare my call</DialogTitle></DialogHeader>
        {ai.busy && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Researching online and reading our history with this company…</p>}
        {ai.err && <p className="text-sm text-red-300" data-testid="prepare-error">{ai.err}</p>}
        {d && (
          <div className="space-y-4 text-sm" data-testid="prepare-briefing">
            {!d.has_history && <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-200" data-testid="no-history-warning">We have no recorded conversations with this company yet.</p>}
            <p className="rounded-xl bg-cyan-400/10 p-4 text-base text-cyan-100" data-testid="prepare-opening">“{d.opening}”</p>
            <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Company overview</p><p className="mt-1">{d.overview}</p></div>
            <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Our previous conversations</p><p className="mt-1">{d.previous_conversations}</p></div>
            <List title="What they told us" items={d.what_they_told_us} />
            <List title="Outstanding promises" items={d.outstanding_promises} testid="prepare-promises" />
            <List title="Suggested questions" items={d.questions} testid="prepare-questions" />
            <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Opportunity</p><p className="mt-1">{d.opportunity}</p></div>
            <List title="Things to avoid" items={d.avoid} />
            <List title="What they're up to right now" items={d.research_hooks} testid="prepare-research-hooks" />
            <Sources items={d.research} at={d.research_at} />
            <Footer d={d} onRegen={ai.go} busy={ai.busy} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const AnalyseCard = ({ cid, warmth }) => {
  const ai = useAi(`/sales/companies/${cid}/analyse`);
  const d = ai.data;
  return (
    <section className="rounded-2xl border bg-card/70 p-5" data-testid="analyse-card">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold">Lead warmth</h2>
        <WarmthBadge w={d ? { score: d.score, label: d.label } : warmth} testid="warmth-badge" />
      </div>
      {!d && <Btn variant="cyan" className="mt-4 w-full justify-center" onClick={() => ai.go()} disabled={ai.busy} data-testid="analyse-btn">
        {ai.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {ai.busy ? "Researching online…" : "Analyse This Opportunity"}</Btn>}
      {ai.err && <p className="mt-3 text-sm text-red-300">{ai.err}</p>}
      {d && (
        <div className="mt-4 space-y-3 text-sm" data-testid="analysis-result">
          {!d.engaged && <p className="rounded-lg bg-white/5 px-3 py-2 text-muted-foreground">Not enough engagement to judge warmth yet. Fit score: <b className="text-foreground">{d.fit}/100</b></p>}
          <p className="text-xs text-muted-foreground">Confidence: <b className="text-foreground">{d.confidence}</b>{d.engaged && d.fit != null ? ` · Fit ${d.fit}/100` : ""}</p>
          <p>{d.reason}</p>
          <List title="Evidence" items={d.score_evidence?.length ? d.score_evidence : d.fit_evidence} testid="analysis-evidence" />
          <List title="Assumptions" items={d.assumptions} />
          <List title="Concerns" items={d.concerns} />
          <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Next action</p><p className="mt-1 font-medium">{d.next_action}</p></div>
          <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Approach</p><p className="mt-1">{d.approach}</p></div>
          <List title="Relevant Atom IP services" items={d.services} />
          <List title="What they're up to right now" items={d.online_signals} testid="analysis-online-signals" />
          <Sources items={d.research} at={d.research_at} />
          <Footer d={d} onRegen={ai.go} busy={ai.busy} />
        </div>
      )}
    </section>
  );
};
